import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {
  DsIconModule,
  DsMessageModule,
  DsSegmentedControlItem,
  DsSegmentedControlModule,
  DsTooltipDirectiveModule,
  SelectListOption,
  SuggestionItem,
  TooltipConfig,
} from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';

import { SuggestedAddress, SuggestionItemWithAddress } from '../../../clients/tolerant-post/model/view.model';
import { AddressField } from '../../../customer/model/address-field.model';
import { CustomerAddress } from '../../../customer/model/customer-information.model';
import { SuggestionOfAddressField } from '../../../customer/services/address.view-service';
import { RegionalAddressComponent } from '../../../form/components/regional-address/regional-address.component';
import { useI18n } from '../../../i18n/services/i18n.service';
import { SingleLineAddressSearchComponent } from '../single-line-address-search/single-line-address-search.component';

export enum ControlItem {
  SEARCH_ADDRESS = 'SEARCH_ADDRESS',
  ADDRESS = 'ADDRESS',
}

@Component({
  selector: 'cdf-smart-address',
  imports: [
    DsIconModule,
    DsMessageModule,
    DsSegmentedControlModule,
    DsTooltipDirectiveModule,
    RegionalAddressComponent,
    SingleLineAddressSearchComponent,
  ],
  template: `
    <h3>{{ isPrimaryContactData() ? i18n('primaryContact.address') : i18n('businessContact.address') }}</h3>
    <div class="body">
      <ds-segmented-control
        [class.has-invalid-address]="shouldShowInvalidAddressBorder()"
        [activeItemId]="selectedTab()"
        [controlItems]="controlItems"
        (controlSelect)="selectTab($event)"
        [fitToContent]="true"
        size="sm"
      />
      @if (selectedTab() === ControlItem.ADDRESS) {
        <cdf-regional-address
          [(address)]="address"
          [ds-tooltip]="i18n('customerInformationDialog.info.enterCountryFirst')"
          [dsTooltipConfig]="missingCountryTooltipConfig()"
          [availableCountries]="availableCountries()"
          [availableProvinces]="availableProvinces()"
          [suggestions]="suggestions()"
          [invalidAddressFields]="invalidAddressFields()"
          [disabled]="disabled()"
          [addressUiFormat]="addressUiFormat()"
          (selectedSuggestionForAddressField)="selectedSuggestionForAddressField.emit($event)"
          (skipAddressValidation)="skipAddressValidation.emit()"
          disableIfNoCountry
        />
      } @else {
        <cdf-single-line-address-search
          [(country)]="singleLineSearchCountry"
          [(singleLineSearch)]="singleLineSearch"
          [overwriteButtonTooltipText]="overwriteButtonTooltipText()"
          [overwriteButtonText]="overwriteButtonText()"
          [suggestions]="singleLineSuggestions()"
          [isSingleLineSuggestionSelected]="isSingleLineSuggestionSelected()"
          [availableCountries]="availableCountries()"
          [disabled]="disabled()"
          (selectSuggestion)="selectSingleLineSuggestion($event)"
          (applyAddressSuggestion)="applySingleLineSuggestion()"
        />
      }
    </div>

    @if (!isSingleLineSuggestionApplied()) {
      <div class="suggestion-not-applied-caution">
        <ds-message
          tone="info"
          variant="slim"
          [messageText]="i18n('customerInformationDialog.info.addressNotAppliedYet')"
        />
      </div>
    }
  `,
  styleUrl: './smart-address.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmartAddressComponent {
  readonly i18n = useI18n();
  readonly logger = inject(NGXLogger);

  readonly addressUiFormat = input.required<AddressField[][]>();

  readonly address = model<CustomerAddress>();
  readonly selectedTab = model<string>(ControlItem.ADDRESS);
  readonly isPrimaryContactData = input(false, { transform: booleanAttribute });

  readonly overwriteButtonTooltipText = input<string>('');
  readonly overwriteButtonText = input<string>('');

  readonly singleLineSearch = model<string>();
  readonly singleLineSearchCountry = model<string | undefined>();

  readonly suggestions = input<SuggestedAddress[]>();
  readonly selectSuggestion = output<SuggestionItem>();
  readonly singleLineSuggestions = input<SuggestionItemWithAddress[]>();
  readonly isSingleLineSuggestionSelected = input(false);
  readonly applyAddressSuggestion = output();
  readonly skipAddressValidation = output();

  readonly selectedSuggestionForAddressField = output<SuggestionOfAddressField>();

  readonly availableCountries = input<SelectListOption[]>([]);
  readonly availableProvinces = input<SelectListOption[]>([]);
  readonly invalidAddressFields = input<AddressField[]>([]);

  readonly disabled = input<boolean>(false);

  readonly isSingleLineSuggestionApplied = signal(true); // Initially true, as initially no warning should be applied
  readonly country = signal<string | undefined>(undefined);

  readonly missingCountryTooltipConfig = computed<TooltipConfig>(() => ({
    disabled: !!this.address()?.country,
    origin: 'right',
    showTriggerDelay: 500,
  }));

  readonly shouldShowInvalidAddressBorder = computed(() => this.invalidAddressFields().length > 0);

  readonly clearSingleLineSuggestionApplied = effect(() => {
    if (this.singleLineSearch() === '') {
      // Remove all warnings about a not-applied single line address
      this.isSingleLineSuggestionApplied.set(true);
    }
  });

  readonly controlItems = [
    {
      icon: 'search',
      label: this.i18n.enum(ControlItem.SEARCH_ADDRESS, 'customerInformationDialog.tab'),
      id: ControlItem.SEARCH_ADDRESS,
    },
    {
      icon: 'map_pin',
      label: this.i18n.enum(ControlItem.ADDRESS, 'customerInformationDialog.tab'),
      id: ControlItem.ADDRESS,
    },
  ];

  selectSingleLineSuggestion(suggestion: SuggestionItem) {
    this.isSingleLineSuggestionApplied.set(false);
    this.selectSuggestion.emit(suggestion);
  }

  applySingleLineSuggestion() {
    this.isSingleLineSuggestionApplied.set(true);
    this.applyAddressSuggestion.emit();
  }

  selectTab(controlItem: DsSegmentedControlItem) {
    const controlItemId = controlItem.id;
    if (!controlItemId) {
      this.logger.warn(`#CDFTF0085 Invalid control item id: ${JSON.stringify(controlItem)}`);
      return;
    }
    const selectedControlItem = ControlItem[controlItemId as keyof typeof ControlItem];
    this.selectedTab.set(selectedControlItem);
  }

  readonly ControlItem = ControlItem;
}
